import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from 'sonner';
import {
  Plus,
  Edit,
  Trash2,
  MoveUp,
  MoveDown,
  Check,
  X,
  Puzzle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";

interface PlatformFeature {
  id: string;
  title: string;
  description: string;
  icon_name: string | null;
  order_index: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const PlatformFeaturesManager = () => {
  const [features, setFeatures] = useState<PlatformFeature[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingFeature, setEditingFeature] = useState<PlatformFeature | null>(null);
  const [newFeature, setNewFeature] = useState({
    title: '',
    description: '',
    icon_name: '',
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('platform_features')
        .select('*')
        .order('order_index', { ascending: true });

      if (error) throw error;
      setFeatures(data as PlatformFeature[]);
    } catch (error) {
      console.error('Error fetching features:', error);
      toast.error('Failed to load platform features');
    } finally {
      setLoading(false);
    }
  };

  const handleEditFeature = (feature: PlatformFeature) => {
    setEditingFeature(feature);
    setIsEditing(true);
  };

  const handleNewFeature = () => {
    setNewFeature({
      title: '',
      description: '',
      icon_name: '',
    });
    setIsEditing(false);
  };

  const saveFeature = async () => {
    try {
      if (isEditing && editingFeature) {
        const { error } = await supabase
          .from('platform_features')
          .update({
            title: editingFeature.title,
            description: editingFeature.description,
            icon_name: editingFeature.icon_name,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingFeature.id);

        if (error) throw error;

        setFeatures(features.map(f => 
          f.id === editingFeature.id ? editingFeature : f
        ));

        toast.success('Feature updated successfully');
      } else {
        const maxOrderIndex = features.length > 0 
          ? Math.max(...features.map(f => f.order_index))
          : -1;

        const { data, error } = await supabase
          .from('platform_features')
          .insert([
            {
              title: newFeature.title,
              description: newFeature.description,
              icon_name: newFeature.icon_name || null,
              order_index: maxOrderIndex + 1,
              is_active: true,
            }
          ])
          .select();

        if (error) throw error;

        setFeatures([...features, data[0] as PlatformFeature]);

        toast.success('Feature added successfully');
      }

      setEditingFeature(null);
      setNewFeature({
        title: '',
        description: '',
        icon_name: '',
      });
    } catch (error) {
      console.error('Error saving feature:', error);
      toast.error('Failed to save feature');
    }
  };

  const toggleFeatureActive = async (id: string, currentState: boolean) => {
    try {
      const { error } = await supabase
        .from('platform_features')
        .update({ is_active: !currentState })
        .eq('id', id);

      if (error) throw error;

      setFeatures(features.map(feature => 
        feature.id === id ? { ...feature, is_active: !currentState } : feature
      ));

      toast.success(`Feature ${!currentState ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error toggling feature:', error);
      toast.error('Failed to update feature');
    }
  };

  const deleteFeature = async (id: string) => {
    if (!confirm('Are you sure you want to delete this feature?')) return;

    try {
      const { error } = await supabase
        .from('platform_features')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setFeatures(features.filter(feature => feature.id !== id));

      toast.success('Feature deleted successfully');
    } catch (error) {
      console.error('Error deleting feature:', error);
      toast.error('Failed to delete feature');
    }
  };

  const moveFeature = async (id: string, direction: 'up' | 'down') => {
    const featureIndex = features.findIndex(f => f.id === id);
    if (featureIndex === -1) return;

    const targetIndex = direction === 'up' ? featureIndex - 1 : featureIndex + 1;
    if (targetIndex < 0 || targetIndex >= features.length) return;

    try {
      const feature = features[featureIndex];
      const targetFeature = features[targetIndex];
      
      const { error: error1 } = await supabase
        .from('platform_features')
        .update({ order_index: targetFeature.order_index })
        .eq('id', feature.id);
      
      const { error: error2 } = await supabase
        .from('platform_features')
        .update({ order_index: feature.order_index })
        .eq('id', targetFeature.id);

      if (error1 || error2) throw error1 || error2;

      const newFeatures = [...features];
      newFeatures[featureIndex] = { ...targetFeature, order_index: feature.order_index };
      newFeatures[targetIndex] = { ...feature, order_index: targetFeature.order_index };
      
      newFeatures.sort((a, b) => a.order_index - b.order_index);
      
      setFeatures(newFeatures);

      toast.success(`Feature moved ${direction}`);
    } catch (error) {
      console.error(`Error moving feature ${direction}:`, error);
      toast.error(`Failed to move feature ${direction}`);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Puzzle className="mr-2 h-5 w-5" />
            Platform Features
          </CardTitle>
          <CardDescription>
            Manage the features showcased to investors and entrepreneurs
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Dialog>
            <DialogTrigger asChild>
              <Button
                className="mb-4"
                onClick={handleNewFeature}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add New Feature
              </Button>
            </DialogTrigger>
            
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{isEditing ? 'Edit Feature' : 'Add New Feature'}</DialogTitle>
                <DialogDescription>
                  {isEditing 
                    ? 'Update the details of this platform feature.' 
                    : 'Add a new feature to showcase on the platform.'}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Title</label>
                  <Input 
                    value={isEditing ? editingFeature?.title : newFeature.title}
                    onChange={(e) => isEditing 
                      ? setEditingFeature({...editingFeature!, title: e.target.value})
                      : setNewFeature({...newFeature, title: e.target.value})
                    }
                    placeholder="Feature title"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Description</label>
                  <Textarea 
                    value={isEditing ? editingFeature?.description : newFeature.description}
                    onChange={(e) => isEditing 
                      ? setEditingFeature({...editingFeature!, description: e.target.value})
                      : setNewFeature({...newFeature, description: e.target.value})
                    }
                    placeholder="Feature description"
                    rows={4}
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Icon Name (Lucide Icon)</label>
                  <Input 
                    value={isEditing 
                      ? (editingFeature?.icon_name || '') 
                      : newFeature.icon_name
                    }
                    onChange={(e) => isEditing 
                      ? setEditingFeature({...editingFeature!, icon_name: e.target.value})
                      : setNewFeature({...newFeature, icon_name: e.target.value})
                    }
                    placeholder="e.g. BarChart2, Users, Shield"
                  />
                  <p className="text-xs text-gray-500">
                    Enter the exact name of a Lucide icon (case-sensitive)
                  </p>
                </div>
              </div>
              
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <DialogClose asChild>
                  <Button onClick={saveFeature}>
                    {isEditing ? 'Update Feature' : 'Add Feature'}
                  </Button>
                </DialogClose>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {loading ? (
            <div className="text-center py-4">Loading features...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Feature</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {features.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-gray-500">
                      No features found. Add your first feature to showcase on the platform.
                    </TableCell>
                  </TableRow>
                ) : (
                  features.map((feature, index) => (
                    <TableRow key={feature.id}>
                      <TableCell className="w-24">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveFeature(feature.id, 'up')}
                            disabled={index === 0}
                          >
                            <MoveUp className="h-4 w-4" />
                          </Button>
                          <span className="w-6 text-center">{index + 1}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveFeature(feature.id, 'down')}
                            disabled={index === features.length - 1}
                          >
                            <MoveDown className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">
                        {feature.title}
                        {feature.icon_name && (
                          <span className="ml-2 text-xs text-gray-500">
                            (Icon: {feature.icon_name})
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="truncate max-w-md">
                          {feature.description}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={feature.is_active}
                            onCheckedChange={(checked) => 
                              toggleFeatureActive(feature.id, feature.is_active)
                            }
                          />
                          <span className={feature.is_active ? 'text-green-600' : 'text-gray-500'}>
                            {feature.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditFeature(feature)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => deleteFeature(feature.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
